import {
    APP_STATE_RESET_TO_SYSTEM_DEFAULT,
    APP_STATE_RESET_TO_USER_DEFAULT,
    APP_STATE_SET_DESERIALIZED_APP_STATE,
} from 'State/action_types';


export const shallowMergeAppStates = (currentState, newState) => {
    /**
     * This function takes the `currentState` object and returns a new merged state object that updates
     * the top level keys on the `currentState` if they are defined in the `newState` object.
     *
     * Example Input:
     *      currentState = {a: 1, b: 2, c: ['one', 'two']}
     *      newState = {a: 4, c: ['three'], d: 2}
     * Example Output:
     *      mergedState = {a: 4, b: 2, c: ['three']}
     */
    const mergedState = { ...currentState };
    const currentAppStateKeys = Object.keys(currentState);
    for (let i = 0; i < currentAppStateKeys.length; i += 1) {
        const key = currentAppStateKeys[i];
        if (key in newState) {
            mergedState[key] = newState[key];
        }
    }
    return mergedState;
};


export default function createReducerWithAppState(reducer, defaultState, userDefaultState) {
    return (state, action) => {
        const { appStateSliceKey } = state;
        // on loading user default state from server side, replace default state
        // https://stackoverflow.com/questions/35622588/
        if (action.type === APP_STATE_RESET_TO_USER_DEFAULT) {
            const currentAppStateSlice = state[appStateSliceKey];
            const userDefaultAppStateSlice = userDefaultState[appStateSliceKey];
            const mergedAppState = shallowMergeAppStates(currentAppStateSlice, userDefaultAppStateSlice);

            const nextState = {
                ...state,
                [appStateSliceKey]: mergedAppState,
            };

            // let any undefined value to get its default
            return reducer(nextState, { type: '@@ycharts/INIT' });
        }

        // Also need a reset to system default, this is used by the Reset All button on the app page
        if (action.type === APP_STATE_RESET_TO_SYSTEM_DEFAULT) {
            const currentAppStateSlice = state[appStateSliceKey];
            const defaultAppStateSlice = defaultState[appStateSliceKey];
            const mergedAppState = shallowMergeAppStates(currentAppStateSlice, defaultAppStateSlice);

            const nextState = {
                ...state,
                [appStateSliceKey]: mergedAppState,
            };

            // let any undefined value to get its default
            return reducer(nextState, { type: '@@ycharts/INIT' });
        }

        // when user setting the url params manually, set it to the state.
        // then dispatch a 'VALIDATE_DESERIALIZED_STATE_SCHEMA' action to validate the state
        if (action.type === APP_STATE_SET_DESERIALIZED_APP_STATE) {
            const currentAppStateSlice = state[appStateSliceKey];
            const { deserializedAppState } = action;
            const mergedAppState = shallowMergeAppStates(currentAppStateSlice, deserializedAppState);

            return {
                ...state,
                [appStateSliceKey]: mergedAppState,
            };
        }

        return reducer(state, action);
    };
}
